import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { useAppState } from "../context/AppStateContext";
import { useToast } from "../context/ToastContext";
import type { EngineerMaster } from "../types";

type Draft = Omit<EngineerMaster, "id" | "createdAt">;

function emptyDraft(): Draft {
  return { fullName: "", phone: "", email: "", employeeCode: "", notes: "" };
}

export function EngineerOnboarding() {
  const { engineerMasters, addEngineerMaster, updateEngineerMaster, deleteEngineerMaster } = useAppState();
  const { showToast } = useToast();
  const [mode, setMode] = useState<"list" | "new" | "edit">("list");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(emptyDraft());
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const sorted = useMemo(
    () => [...engineerMasters].sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    [engineerMasters],
  );

  function startNew() {
    setDraft(emptyDraft());
    setEditingId(null);
    setMode("new");
  }

  function startEdit(m: EngineerMaster) {
    setEditingId(m.id);
    setDraft({
      fullName: m.fullName,
      phone: m.phone,
      email: m.email,
      employeeCode: m.employeeCode,
      notes: m.notes,
    });
    setMode("edit");
  }

  async function save() {
    if (!draft.fullName.trim() || !draft.phone.trim()) {
      showToast("Full name and phone are required.", "error");
      return;
    }
    if (mode === "new") {
      const id = await addEngineerMaster(draft);
      if (!id) return;
    } else if (mode === "edit" && editingId) {
      const ok = await updateEngineerMaster(editingId, draft);
      if (!ok) return;
    }
    setMode("list");
    setEditingId(null);
    setDraft(emptyDraft());
  }

  async function remove(id: string) {
    setDeletingId(id);
    try {
      await deleteEngineerMaster(id);
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <>
      <PageHeader
        breadcrumb={[{ label: "Home", to: "/" }, { label: "Engineer onboarding" }]}
        title="Engineer onboarding"
      />
      <p className="section-lead">
        Register site engineers for assignment to beneficiaries. This list is separate from{" "}
        <Link to="/agent-master">Agent master</Link>. Assign engineers from the <Link to="/subcontractor-to-beneficiary">Beneficiaries</Link> list.
      </p>

      {mode === "list" ? (
        <div className="card card--flush card--elevated">
          <div className="table-toolbar table-toolbar--stack">
            <div className="table-toolbar__top">
              <span className="table-toolbar__count">{sorted.length} engineer(s)</span>
              <button type="button" className="btn btn--primary btn--tg" onClick={startNew}>
                Add engineer
              </button>
            </div>
          </div>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Employee code</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sorted.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="data-table__empty">
                      No engineers yet. Click <strong>Add engineer</strong>.
                    </td>
                  </tr>
                ) : (
                  sorted.map((m) => (
                    <tr key={m.id}>
                      <td>{m.fullName}</td>
                      <td className="mono">{m.phone}</td>
                      <td>{m.email || "—"}</td>
                      <td className="mono">{m.employeeCode || "—"}</td>
                      <td>
                        <button type="button" className="btn btn--secondary btn--sm btn--tg" onClick={() => startEdit(m)}>
                          Edit
                        </button>{" "}
                        <button
                          type="button"
                          className="btn btn--secondary btn--sm btn--tg"
                          disabled={deletingId === m.id}
                          onClick={() => remove(m.id)}
                        >
                          {deletingId === m.id ? "Removing…" : "Remove"}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="card card--gov card--elevated">
          <h2 className="card__title card__title--gov">{mode === "new" ? "New engineer" : "Edit engineer"}</h2>
          <div className="field-grid">
            <label className="field field--full">
              <span>Full name</span>
              <input
                className="form-control"
                value={draft.fullName}
                onChange={(e) => setDraft((d) => ({ ...d, fullName: e.target.value }))}
                autoComplete="off"
              />
            </label>
            <label className="field">
              <span>Phone</span>
              <input
                className="form-control mono"
                value={draft.phone}
                onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))}
                autoComplete="off"
              />
            </label>
            <label className="field">
              <span>Email</span>
              <input
                className="form-control"
                value={draft.email}
                onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
                autoComplete="off"
              />
            </label>
            <label className="field">
              <span>Employee code</span>
              <input
                className="form-control mono"
                value={draft.employeeCode}
                onChange={(e) => setDraft((d) => ({ ...d, employeeCode: e.target.value }))}
                autoComplete="off"
              />
            </label>
            <label className="field field--full">
              <span>Notes</span>
              <textarea
                className="form-control"
                rows={3}
                value={draft.notes}
                onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
              />
            </label>
          </div>
          <div className="toolbar-row" style={{ marginTop: "1rem" }}>
            <button type="button" className="btn btn--secondary btn--tg" onClick={() => setMode("list")}>
              Cancel
            </button>
            <button type="button" className="btn btn--primary btn--tg" onClick={() => void save()}>
              Save
            </button>
          </div>
        </div>
      )}
    </>
  );
}
